// Given an id, a list of ids, a set of selected ids and a modifier, this function returns a new Set of selected ids.

// If modifier is falsey:
//  - if the id is already selected and there is only one selected id, it deselects it. Otherwise, it selects it.
//
// If modifier is SHIFT and id is not already selected:
//  - the id is before the first selected id, it selects all the ids between the id and the first selected id.
//  - the id is after the last selected id, it selects all the ids between the id and the last selected id.
//
// If modifier is CTRL:
//  - if the id is already selected, it deselects it.
//  - if the id is not selected, it selects it.
//

export type Modifier = "CTRL" | "SHIFT";

export const getModifier = (
  event: React.MouseEvent<Element, MouseEvent>,
): Modifier | null => {
  if (event.metaKey || event.ctrlKey) {
    return "CTRL";
  }

  if (event.shiftKey) {
    return "SHIFT";
  }

  return null;
};

export const selectInList = <IdType>(
  id: IdType,
  ids: Set<IdType>,
  selectedIds: Set<IdType>,
  modifier?: Modifier,
): Set<IdType> => {
  const isAlreadySelected = selectedIds.has(id);
  const areMultipleItemsSelected = selectedIds.size > 1;

  if (!modifier) {
    return isAlreadySelected && !areMultipleItemsSelected
      ? new Set()
      : new Set([id]);
  }

  if (modifier === "SHIFT" && !isAlreadySelected) {
    const idsArray = Array.from(ids);
    const selectedIndexes = Array.from(selectedIds)
      .map((selectedFileId) => idsArray.indexOf(selectedFileId))
      .filter((index) => index !== -1);

    selectedIndexes.push(idsArray.indexOf(id));

    const minIndex = Math.min(...selectedIndexes);
    const maxIndex = Math.max(...selectedIndexes);

    return new Set(idsArray.slice(minIndex, maxIndex + 1));
  }

  if (modifier === "CTRL") {
    const newSelectedIds = new Set(selectedIds);

    if (isAlreadySelected) {
      newSelectedIds.delete(id);
    } else {
      newSelectedIds.add(id);
    }
    return newSelectedIds;
  }

  return selectedIds;
};
